/**
 * Leave lifecycle system-message kinds.
 *
 * The backend fans these out as `message_type: 'system'` rows into EVERY
 * conversation the on-leave user belongs to (DMs + groups + channels). They
 * render in-thread as a pill (see SystemMessagePill), but must NOT be treated
 * like a normal message: they must not become a conversation's last-message
 * preview, bump it to the top of the sidebar, or raise unread counts — doing so
 * floods the list with the same "<name> is on leave / is back" line everywhere.
 */
export const LEAVE_SYSTEM_KINDS = [
  'leave_start',
  'leave_end',
  'leave_approved',
  'leave_cancelled',
  'away_start',
  'away_end',
] as const;

export function isLeaveSystemMessage(
  msg: { message_type?: string; body?: unknown } | null | undefined
): boolean {
  if (!msg || msg.message_type !== 'system') return false;
  const kind = (msg.body as { kind?: string } | null | undefined)?.kind;
  return !!kind && (LEAVE_SYSTEM_KINDS as readonly string[]).includes(kind);
}

type LeaveStartMessage = {
  message_type?: string;
  body?: { kind?: string; end_date?: string | null } | null;
} | null | undefined;

function localTodayISO(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * A delayed or legacy leave_start can arrive after its inclusive leave window
 * ended. Treat it as a no-op so it cannot revive a leave badge in Messenger.
 */
export function isCurrentLeaveStart(
  msg: LeaveStartMessage,
  todayISO: string = localTodayISO(),
): boolean {
  const body = msg?.body;
  if (msg?.message_type !== 'system' || body?.kind !== 'leave_start') return true;
  const endDate = body.end_date?.slice(0, 10);
  return !endDate || endDate >= todayISO;
}

export function shouldRenderInMessageTimeline(
  msg: { message_type?: string; body?: unknown } | null | undefined
): boolean {
  return isCurrentLeaveStart(msg as LeaveStartMessage);
}
